import { ref, computed } from 'vue';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  deleteDoc,
} from '@/services/legacyBrowserPersistenceBoundary';
import { toast } from 'vue3-toastify';
import { parseUnits } from 'ethers';

export function nftTimelineDocIdForNft(nftId) {
  const normalizedNftId = typeof nftId === 'string' ? nftId.trim() : '';
  if (!normalizedNftId) {
    throw new TypeError('nftId is required to create a timeline document ID');
  }

  const timelineDocId = `nft_${encodeURIComponent(normalizedNftId)}`;
  if (timelineDocId.length > 1400) {
    throw new RangeError('nftId is too long to create a Firestore document ID');
  }

  return timelineDocId;
}

/**
 * Composable for NFT listing functionality
 * Handles listing, editing, and delisting NFTs on the marketplace
 * 
 * @param {Object} params
 * @param {Ref} params.currentUserUid - Current authenticated user UID
 * @param {Ref} params.nftData - NFT data object
 */
export function useNFTListing({ currentUserUid, nftData }) {
  const isListing = ref(false);
  const listingPrice = ref(0);
  const termsAcknowledged = ref(false);
  
  // Get network details from NFT
  const chainId = computed(() => nftData.value?.chainId || '0x2105');
  
  const nativeCurrency = computed(() => {
    const currencies = {
      '0x1': 'ETH',
      '0x2105': 'ETH', // Base uses ETH
      '0x89': 'MATIC',
      '0xa': 'ETH', // Optimism
      '0xa4b1': 'ETH', // Arbitrum
    };
    return currencies[chainId.value] || 'ETH';
  });
  
  const networkName = computed(() => {
    const names = {
      '0x1': 'Ethereum',
      '0x2105': 'Base',
      '0x89': 'Polygon',
      '0xa': 'Optimism',
      '0xa4b1': 'Arbitrum',
    };
    return names[chainId.value] || 'Unknown';
  });
  
  // Fee calculations (read from NFT metadata or defaults)
  const royaltyBps = computed(() => nftData.value?.royaltyBps || 500); // 5%
  const platformFeeBps = computed(() => 700); // 7% platform fee
  
  const royaltyPercent = computed(() => (royaltyBps.value / 100).toFixed(2));
  const platformFeePercent = computed(() => (platformFeeBps.value / 100).toFixed(2));
  
  const creatorRoyaltyDisplay = computed(() => {
    const amount = (listingPrice.value * royaltyBps.value) / 10000;
    return amount.toFixed(4);
  });
  
  const platformFeeDisplay = computed(() => {
    const amount = (listingPrice.value * platformFeeBps.value) / 10000;
    return amount.toFixed(4);
  });
  
  const netProceedsDisplay = computed(() => {
    const totalFees = (royaltyBps.value + platformFeeBps.value) / 10000;
    const netAmount = listingPrice.value * (1 - totalFees);
    return netAmount.toFixed(4);
  });
  
  // Validation
  const isPriceValid = computed(() => {
    return listingPrice.value >= 0.001 && listingPrice.value <= 1000;
  });
  
  const canList = computed(() => {
    return isPriceValid.value && termsAcknowledged.value && !isListing.value;
  });
  
  /**
   * Check if NFT is already listed
   * @param {string} nftId - Composite NFT ID
   * @returns {Promise<boolean>}
   */
  const isNFTListed = async (nftId) => {
    try {
      const listingRef = doc(null, 'nftListings', nftId);
      const listingSnap = await getDoc(listingRef);
      
      if (!listingSnap.exists()) return false;
      
      const data = listingSnap.data();
      return data.listingStatus === 'active';
    } catch (error) {
      console.error('[useNFTListing] Error checking NFT listing status:', error);
      return false;
    }
  };
  
  /**
   * Get existing listing data for an NFT
   * @param {string} nftId - Composite NFT ID
   * @returns {Promise<Object|null>}
   */
  const getListingData = async (nftId) => {
    try {
      const listingRef = doc(null, 'nftListings', nftId);
      const listingSnap = await getDoc(listingRef);
      
      if (!listingSnap.exists()) return null;
      
      return listingSnap.data();
    } catch (error) {
      console.error('[useNFTListing] Error fetching listing data:', error);
      return null;
    }
  };
  
  /**
   * Create NFT listing in marketplace
   * @returns {Promise<Object>} Result object with success status
   */
  const listNFT = async () => {
    if (!canList.value) {
      toast.error('Please complete all required fields');
      return { success: false, error: 'Validation failed' };
    }
    
    isListing.value = true;
    
    try {
      const nft = nftData.value;
      if (!nft) {
        throw new Error('NFT data not available');
      }
      
      const nftId = `nft-${nft.chainId}-${nft.contractAddress}-${nft.tokenId}`;
      
      // Check if already listed
      const alreadyListed = await isNFTListed(nftId);
      if (alreadyListed) {
        toast.error('This NFT is already listed');
        return { success: false, error: 'Already listed' };
      }
      
      // Convert price to wei for precision
      const priceWei = parseUnits(listingPrice.value.toString(), 18);
      
      // Replays and partial-write retries must converge on one listing document.
      const timelineDocId = nftTimelineDocIdForNft(nftId);
      
      // Create itemsTimeline document
      const timelineData = {
        originalPath: `nfts/${nftId}`,
        type: 'nft',
        nftId,
        contractAddress: nft.contractAddress,
        tokenId: nft.tokenId,
        chainId: nft.chainId,
        
        title: nft.name || `NFT #${nft.tokenId}`,
        description: nft.description || '',
        thumbnail: nft.image || nft.thumbnail,
        images: [nft.image || nft.thumbnail].filter(Boolean),
        
        listingPrice: listingPrice.value,
        listingPriceWei: priceWei.toString(),
        currency: nativeCurrency.value,
        coinSymbol: nativeCurrency.value,
        price: 0, // Not Unit coins
        
        owner: nft.owner || nft.ownerAddress,
        ownerUid: currentUserUid.value,
        organisation: nft.organisation || nft.ownerOrgUID || null,
        
        listedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        isPublished: true,
        listingStatus: 'active',
        
        blockchainData: {
          mintTxHash: nft.mintTxHash || null,
          mintedAt: nft.mintedAt || null,
          royaltyBps: royaltyBps.value,
          platformFeeBps: platformFeeBps.value,
        },
        
        category: nft.category || 'NFT',
        mediaType: 'nft',
        tags: nft.tags || [],
        location: null,
        
        isNFTListing: true,
        requiresWalletPurchase: true,
      };
      
      console.log('[useNFTListing] Creating timeline document:', timelineDocId);
      await setDoc(doc(null, 'itemsTimeline', timelineDocId), timelineData);
      
      // Create nftListings tracking document
      const listingTrackingData = {
        nftId,
        contractAddress: nft.contractAddress,
        tokenId: nft.tokenId,
        chainId: nft.chainId,
        
        timelineDocId,
        listingStatus: 'active',
        
        listingPrice: listingPrice.value,
        listingPriceWei: priceWei.toString(),
        currency: nativeCurrency.value,
        
        currentOwner: nft.owner || nft.ownerAddress,
        ownerUid: currentUserUid.value,
        
        listedAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        
        listingHistory: [],
      };
      
      console.log('[useNFTListing] Creating listing tracking document:', nftId);
      await setDoc(doc(null, 'nftListings', nftId), listingTrackingData);
      
      toast.success('NFT listed successfully!');
      return { success: true, timelineDocId };
      
    } catch (error) {
      console.error('[useNFTListing] Error listing NFT:', error);
      toast.error(error.message || 'Failed to list NFT');
      return { success: false, error };
    } finally {
      isListing.value = false;
    }
  };
  
  /**
   * Update existing listing price
   * @param {string} nftId - Composite NFT ID
   * @returns {Promise<Object>} Result object with success status
   */
  const updateListing = async (nftId) => {
    if (!isPriceValid.value) {
      toast.error('Please enter a valid price');
      return { success: false, error: 'Invalid price' };
    }
    
    isListing.value = true;
    
    try {
      const listingData = await getListingData(nftId);
      
      if (!listingData) {
        throw new Error('Listing not found');
      }
      
      if (listingData.listingStatus !== 'active') {
        throw new Error('Listing is not active');
      }
      
      const priceWei = parseUnits(listingPrice.value.toString(), 18);
      const timelineDocId = listingData.timelineDocId;
      
      // Update itemsTimeline document
      await setDoc(doc(null, 'itemsTimeline', timelineDocId), {
        listingPrice: listingPrice.value,
        listingPriceWei: priceWei.toString(),
        lastUpdated: serverTimestamp(),
      }, { merge: true });
      
      // Update nftListings document
      await setDoc(doc(null, 'nftListings', nftId), {
        listingPrice: listingPrice.value,
        listingPriceWei: priceWei.toString(),
        lastUpdated: serverTimestamp(),
        listingHistory: [
          ...(listingData.listingHistory || []),
          {
            price: listingData.listingPrice,
            priceWei: listingData.listingPriceWei,
            updatedAt: serverTimestamp(),
            reason: 'price_change',
          }
        ],
      }, { merge: true });
      
      toast.success('Listing updated successfully!');
      return { success: true };
      
    } catch (error) {
      console.error('[useNFTListing] Error updating listing:', error);
      toast.error(error.message || 'Failed to update listing');
      return { success: false, error };
    } finally {
      isListing.value = false;
    }
  };
  
  /**
   * Delist NFT from marketplace
   * @param {string} nftId - Composite NFT ID
   * @returns {Promise<Object>} Result object with success status
   */
  const delistNFT = async (nftId) => {
    try {
      const listingData = await getListingData(nftId);
      
      if (!listingData) {
        toast.error('Listing not found');
        return { success: false, error: 'Listing not found' };
      }
      
      // Update nftListings status
      await setDoc(doc(null, 'nftListings', nftId), {
        listingStatus: 'delisted',
        lastUpdated: serverTimestamp(),
      }, { merge: true });
      
      // Delete from itemsTimeline
      const timelineDocId = listingData.timelineDocId;
      await deleteDoc(doc(null, 'itemsTimeline', timelineDocId));
      
      toast.success('NFT delisted successfully');
      return { success: true };
    } catch (error) {
      console.error('[useNFTListing] Error delisting NFT:', error);
      toast.error('Failed to delist NFT');
      return { success: false, error };
    }
  };
  
  return {
    // State
    isListing,
    listingPrice,
    termsAcknowledged,
    
    // Computed
    chainId,
    nativeCurrency,
    networkName,
    royaltyPercent,
    platformFeePercent,
    creatorRoyaltyDisplay,
    platformFeeDisplay,
    netProceedsDisplay,
    isPriceValid,
    canList,
    
    // Methods
    isNFTListed,
    getListingData,
    listNFT,
    updateListing,
    delistNFT,
  };
}
